import {
    Button,
    Card,
    Flex,
    Grid,
    Image,
    PasswordInput,
    Popover,
    Text,
    TextInput
} from "@mantine/core";
import { AlternateEmail, Lock } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { Auth, showErrorNotification } from "@therapprove/common";
import {
    playwright_login_card,
    playwright_login_loginButton,
    playwright_login_passwordInput,
    playwright_login_usernameInput
} from "@therapprove/playwright";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import nellyLogin from "../../assets/nelly-login.png";
import logoFull from "../../assets/therapprove-logo-transparent-black.png";
import { loginValidation } from "./Login.validation";

const auth = new Auth();

export const Login = () => {
    const navigate = useNavigate();
    const emailRef = useRef<HTMLInputElement>(null);
    const [password, setPassword] = useState(""); // We need to use state for this variable so we can persist the password, even after the field disappears (when a new password is required).
    const newPasswordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showHelpNumber, setShowHelpNumber] = useState(false);

    const onSubmit = async (event) => {
        setIsLoggingIn(true);
        event.preventDefault();

        // Check to make sure ref values are defined, if not, assign null
        const email = emailRef.current ? emailRef.current.value.trim() : null;
        const newPassword = newPasswordRef.current
            ? newPasswordRef.current.value.trim()
            : null;
        const confirmPassword = confirmPasswordRef.current
            ? confirmPasswordRef.current.value.trim()
            : null;

        // Validate the form data
        const isFormValid = await loginValidation(
            {
                email,
                password,
                newPassword,
                confirmPassword
            },
            newPasswordRequired
        );

        // If there are any validation errors on the form, don't try to login
        if (!isFormValid) {
            console.error("There are errors with the login form data.");
            setIsLoggingIn(false);
            return;
        }

        auth.authenticate({
            username: email,
            password,
            newPassword
        })
            .then((data) => {
                if (data) {
                    setNewPasswordRequired(!!data.new_password_required);

                    // If no new password is required, go to the app.
                    if (!data.new_password_required) {
                        navigate("/dashboard");
                    }
                }
            })
            .catch((error) => {
                showErrorNotification(error.message);
            })
            .finally(() => {
                setIsLoggingIn(false);
            });
    };

    // When the user presses 'Enter' on the login form, we actually try to login.
    const handleKeyDown = (e) => {
        if (e.keyCode === 13) {
            onSubmit(e);
        }
    };

    return (
        <Grid style={{ height: "100vh", overflow: "hidden" }}>
            <Grid.Col
                span={{ base: 12, lg: 6 }}
                visibleFrom="lg"
                style={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingTop: "5rem",
                    overflow: "hidden"
                }}
            >
                <Image
                    src={nellyLogin}
                    style={{
                        flex: 1,
                        borderRadius: "0.65rem",
                        height: "30rem",
                        maxWidth: "50rem",
                        minWidth: "25rem",
                        objectFit: "contain"
                    }}
                />
            </Grid.Col>
            <Grid.Col
                span={{ base: 12, lg: 6 }}
                style={{ height: "105vh", overflow: "auto" }}
            >
                <Card
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        backgroundColor: "var(--mantine-color-primary-1)",
                        height: "100%",
                        width: "100%",
                        overflow: "auto"
                    }}
                    data-testid={playwright_login_card}
                >
                    <Image
                        src={logoFull}
                        style={{
                            width: "60%",
                            marginTop: "1rem",
                            alignSelf: "center"
                        }}
                    />
                    <Text
                        variant="body"
                        style={{
                            marginTop: "5rem",
                            alignSelf: "center",
                            fontSize: "2rem",
                            fontWeight: 700
                        }}
                    >
                        Sign In
                    </Text>
                    <form onKeyDown={handleKeyDown}>
                        <TextInput
                            data-hj-allow // Allow this field to be tracked by Hotjar
                            size="lg"
                            radius="md"
                            leftSection={<SvgIcon component={AlternateEmail} />}
                            placeholder="Email Address"
                            autoFocus
                            style={{
                                marginTop: "1rem",
                                marginLeft: "auto",
                                marginRight: "auto",
                                width: "75%"
                            }}
                            ref={emailRef}
                            data-testid={playwright_login_usernameInput}
                        />
                        {/* If a new password is NOT required, show the password field.
                If it is required, show the New and Confirm password fields. */}
                        {!newPasswordRequired && (
                            <PasswordInput
                                size="lg"
                                radius="md"
                                leftSection={<SvgIcon component={Lock} />}
                                placeholder="Password"
                                style={{
                                    marginTop: "1rem",
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    width: "75%"
                                }}
                                onChange={(event) =>
                                    setPassword(event.target.value)
                                }
                                data-testid={playwright_login_passwordInput}
                            />
                        )}

                        {newPasswordRequired && (
                            <>
                                <PasswordInput
                                    size="lg"
                                    radius="md"
                                    leftSection={<SvgIcon component={Lock} />}
                                    placeholder="New Password"
                                    style={{
                                        marginTop: "1rem",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        width: "75%"
                                    }}
                                    ref={newPasswordRef}
                                />
                                <PasswordInput
                                    size="lg"
                                    radius="md"
                                    leftSection={<SvgIcon component={Lock} />}
                                    placeholder="Confirm Password"
                                    style={{
                                        marginTop: "1rem",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        width: "75%"
                                    }}
                                    ref={confirmPasswordRef}
                                />
                            </>
                        )}

                        <Flex justify="center" style={{ width: "100%" }}>
                            <Button
                                size="lg"
                                w="40%"
                                mt={newPasswordRequired ? "1rem" : "3rem"}
                                loading={isLoggingIn}
                                onClick={onSubmit}
                                data-testid={playwright_login_loginButton}
                            >
                                {newPasswordRequired
                                    ? "Change Password"
                                    : "Login"}
                            </Button>
                        </Flex>
                        {newPasswordRequired ? (
                            <Text
                                variant="body"
                                style={{
                                    marginTop: "2rem",
                                    marginLeft: "auto",
                                    marginRight: "auto"
                                }}
                            >
                                Password must be at least 8 characters in length
                                and contain a number, a capital letter, a lower
                                case letter, and a special character.
                            </Text>
                        ) : null}
                    </form>
                    <Link
                        to={"/forgot-password"}
                        style={{
                            marginTop: "2rem",
                            marginLeft: "auto",
                            marginRight: "auto"
                        }}
                    >
                        Forgot Password?
                    </Link>
                    <Flex
                        justify="center"
                        style={{
                            marginTop: "1.5rem",
                            marginLeft: "auto",
                            marginRight: "auto"
                        }}
                    >
                        <Popover
                            position="bottom"
                            withArrow
                            shadow="md"
                            opened={showHelpNumber}
                            onChange={setShowHelpNumber}
                        >
                            <Popover.Target>
                                <Text
                                    variant="body"
                                    style={{
                                        fontSize: "0.875rem",
                                        color: "dimmed",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        textDecorationColor: "transparent",
                                        transition: "all 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.textDecorationColor =
                                            "currentColor";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.textDecorationColor =
                                            "transparent";
                                    }}
                                    onClick={() =>
                                        setShowHelpNumber(!showHelpNumber)
                                    }
                                >
                                    Need help?
                                </Text>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Text
                                    variant="body"
                                    size="sm"
                                    style={{ textAlign: "center" }}
                                >
                                    Call{" "}
                                    <Text
                                        variant="body"
                                        component="span"
                                        style={{
                                            fontWeight: 600,
                                            textDecoration: "underline",
                                            cursor: "pointer"
                                        }}
                                        onClick={() =>
                                            window.open("tel:3172103373")
                                        }
                                    >
                                        (317) 210-3373
                                    </Text>
                                </Text>
                                <Text
                                    variant="body"
                                    size="sm"
                                    style={{ textAlign: "center" }}
                                >
                                    Email{" "}
                                    <Text
                                        variant="body"
                                        component="span"
                                        style={{
                                            fontWeight: 600,
                                            textDecoration: "underline",
                                            cursor: "pointer"
                                        }}
                                        onClick={() =>
                                            window.open(
                                                "mailto:support@therapprove.com"
                                            )
                                        }
                                    >
                                        support@therapprove.com
                                    </Text>
                                </Text>
                            </Popover.Dropdown>
                        </Popover>
                    </Flex>
                </Card>
            </Grid.Col>
        </Grid>
    );
};
